import { z } from 'zod';
/**
 * Call activity contract v1, shared by Revcall and the Swoft framework.
 *
 * Source of truth: docs/integrations/swoft-call-activity-contract-v1.md.
 * This file mirrors it, field for field. Change the document first, then
 * this schema, never the other way round.
 *
 * Direction 1 (Revcall -> Swoft): CallActivityPayloadSchema is the body of
 * POST {swoftBaseUrl}/api/crm/call-activities, idempotent on externalRef.
 * Direction 2 (Swoft -> Revcall): SwoftInboundEventSchema is the body of
 * POST {revcallBaseUrl}/api/integrations/swoft/events, signed with HMAC.
 */
export const CallActivityDirectionSchema = z.enum(['outbound', 'inbound']);
export const CallActivityOutcomeSchema = z.enum([
    'contacted',
    'not_reachable',
    'not_interested',
    'meeting_booked',
    'callback',
    'wrong_number',
]);
export const CallActivityMeetingTypeSchema = z.enum(['Call', 'Video', 'InPerson']);
export const CallActivityCallerSchema = z.object({
    externalId: z.string(),
    name: z.string(),
    email: z.string(),
});
export const CallActivityProspectSchema = z.object({
    // The ProspectQueue row id. Null when the call was placed to a number that
    // is not in any queue (the contract example shows the common case).
    externalId: z.string().nullable(),
    firstName: z.string(),
    lastName: z.string(),
    company: z.string(),
    title: z.string(),
    phone: z.string().min(1),
    email: z.string(),
    website: z.string(),
});
export const CallActivityMeetingSchema = z.object({
    at: z.string().datetime(),
    type: CallActivityMeetingTypeSchema,
    durationMinutes: z.number().int().positive().nullable(),
    location: z.string().nullable(),
    // Who, on the client side, holds this meeting: a Swoft Person id picked
    // once per client in the settings screen. OPTIONAL, and absent rather than
    // null when no one was picked: the key missing is what tells Swoft to keep
    // today's behaviour and leave the appointment without a holder.
    hostPartyId: z.string().min(1).optional(),
});
export const CallActivityPayloadSchema = z.object({
    externalRef: z.string().regex(/^revcall:call:[^:\s]+$/),
    source: z.literal('revcall'),
    campaign: z.string().nullable(),
    occurredAt: z.string().datetime(),
    direction: CallActivityDirectionSchema,
    outcome: CallActivityOutcomeSchema,
    durationSeconds: z.number().int().min(0),
    caller: CallActivityCallerSchema,
    prospect: CallActivityProspectSchema,
    note: z.string(),
    summary: z.string(),
    nextSteps: z.string(),
    painPoints: z.array(z.string()),
    interests: z.array(z.string()),
    objections: z.array(z.string()),
    recordingRef: z.string().nullable(),
    // Envoyé depuis le 2026-09-30. Il était toujours null, la base légale du
    // transfert n'étant pas établie (ADR-035, question ouverte 4) ; Kevin a
    // confirmé ce jour-là qu'elle est couverte en amont. Swoft l'accepte déjà :
    // il est posé sur la Communication, comme texte et non comme pièce jointe.
    transcript: z.string().nullable(),
    meeting: CallActivityMeetingSchema.nullable(),
    followUpAt: z.string().datetime().nullable(),
});
/** Response of a 200/201 from POST /api/crm/call-activities. */
export const CallActivityResponseSchema = z.object({
    communicationId: z.string().optional(),
    // Null when the prospect had no email (no Person party, contract step 2).
    prospectPersonId: z.string().nullable().optional(),
    prospectOrganisationId: z.string().optional(),
    appointmentId: z.string().optional(),
    opportunityId: z.string().optional(),
    created: z.boolean().optional(),
});
/**
 * Meeting status follow-up (Revcall -> Swoft), not consumed by the framework
 * yet. Delivered to POST {swoftBaseUrl}/api/crm/call-activities/meeting-status;
 * a 404 means "not supported yet" and keeps the row pending.
 */
export const MeetingStatusPayloadSchema = z.object({
    externalRef: z.string().nullable(),
    meetingId: z.string(),
    status: z.enum(['no_show', 'completed', 'cancelled']),
    at: z.string().datetime(),
});
// ─── Direction 2: Swoft -> Revcall ─────────────────────────────────────────
export const SwoftInboundEventTypeSchema = z.enum([
    'appointment.no_show',
    'opportunity.stage_changed',
]);
export const SwoftInboundEventSchema = z.object({
    eventId: z.string().min(1),
    type: SwoftInboundEventTypeSchema,
    occurredAt: z.string(),
    organisationId: z.string().min(1),
    externalRef: z.string().nullable().optional(),
    data: z.record(z.unknown()).default({}),
});
export const SWOFT_SIGNATURE_HEADER = 'x-swoft-signature';
export const SWOFT_TIMESTAMP_HEADER = 'x-swoft-timestamp';
export const SWOFT_REPLAY_WINDOW_SECONDS = 300;
//# sourceMappingURL=swoft-call-activity.schema.js.map